import React from 'react';
import { LenderResponse } from '../api/claims';

interface Props {
  responses?: LenderResponse[];
  /** The claim's submissionOutcomeDate — the date WE received the lender's
   * response. Anchors the 3-year limitation timer. Optional: claims whose
   * response import predates the outcome-date backfill simply show no timer. */
  outcomeDate?: string | null;
  /** Commission values extracted from ordinary CAF correspondence by the
   * curation data-point writer (as opposed to a lender spreadsheet import). */
  commissionAmount?: number | string | null;
  commissionInformedDate?: string | null;
}

/** Parse a date the lender sent us, as a LOCAL calendar date.
 *
 * Two traps here, both silent:
 *  1. Spreadsheet text arrives as UK DD/MM/YYYY, which `new Date()` reads as US
 *     MM/DD/YYYY — "03/08/2026" (3 August) would render as 08/03/2026.
 *  2. `new Date("2026-08-01")` is parsed as UTC midnight per ECMA-262, so west
 *     of Greenwich it renders as the PREVIOUS day. The import writes ISO for
 *     every real Excel date cell, so this branch is the common one, not the
 *     rare one — it must build a local date exactly as the UK branch does or
 *     the two disagree by a day.
 * These values feed limitation reasoning, so a wrong-but-plausible date is
 * worse than a visibly raw one: both branches construct from parts, and
 * anything else falls back to the raw string. */
const parseLenderDate = (v: string): Date | null => {
  const s = v.trim();
  const uk = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/);
  let y: number, m: number, d: number;
  if (uk) {
    [, d, m, y] = uk.map(Number) as unknown as [never, number, number, number];
  } else if (iso) {
    [, y, m, d] = iso.map(Number) as unknown as [never, number, number, number];
  } else {
    return null;
  }
  const dt = new Date(y, m - 1, d);
  // Rejects 31/02 and friends: JS would roll them forward silently.
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
    ? dt
    : null;
};

/** `null` is in the signature because it genuinely arrives: the Santander
 * import stores absent commission as JSON null (Python None), not "". The
 * runtime check already handled it — only the type was narrower than the
 * behaviour. */
const isBlank = (v?: number | string | null) =>
  v === undefined || v === null || v === '' ||
  ['none', 'null', 'n/a'].includes(String(v).trim().toLowerCase());

const fmtDate = (v?: string) => {
  if (isBlank(v)) return '—';
  const d = parseLenderDate(String(v));
  return d ? d.toLocaleDateString('en-GB') : String(v);
};

/** Keep the sign and the pence, and NEVER invent a number.
 *
 * A leading minus is meaningful (a refund or credit) and stripping it flips the
 * value, while the default toLocaleString drops the decimals off "150.55".
 *
 * The important rule is the fallback: strip-then-Number() cannot be trusted,
 * because Number('') is +0 rather than NaN — so "TBC", "Not held" or "£" would
 * all render a confident "£0.00" over a value we do not have, and a reader has
 * no way to tell that from a real zero. Accounting parentheses and trailing
 * signs mean the same digits with the opposite sign. So: recognise the shapes we
 * genuinely understand, and show anything else verbatim. */
const MONEY_RE = /^-?\d+(?:\.\d+)?$/;
// Thousands separators only in genuine group-of-three positions. "1,234,567.89"
// passes; "1.234,56" (European) does not, so it falls through to verbatim rather
// than silently becoming £1.23 -- which was the exact failure the fallback exists
// to prevent.
const GROUPED_RE = /^-?\d{1,3}(?:,\d{3})*(?:\.\d+)?$/;

const parseMoney = (v: number | string): number | null => {
  if (typeof v === 'number') return isFinite(v) ? v : null;
  let s = String(v).trim().replace(/^£\s*/, '');
  let negative = false;
  const bracketed = s.match(/^\((.*)\)$/);      // (500) = -500
  if (bracketed) {
    negative = true;
    s = bracketed[1].trim();
  }
  if (s.endsWith('-')) {                        // 500- = -500
    negative = true;
    s = s.slice(0, -1).trim();
  }
  if (s.includes(',')) {
    if (!GROUPED_RE.test(s)) return null;       // ambiguous separators -> verbatim
    s = s.replace(/,/g, '');
  }
  if (!MONEY_RE.test(s)) return null;           // includes '' -> not a number
  const n = Number(s);
  if (!isFinite(n)) return null;
  return negative ? -Math.abs(n) : n;
};

const fmtMoney = (v?: number | string | null) => {
  if (isBlank(v)) return '—';
  const n = parseMoney(v as number | string);
  if (n === null) return String(v);             // raw, so it reads as "unparsed"
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Blank-safe passthrough. Note `fmt(0)` is "0", not "—": a real 0% APR or a
 * £0 balloon is data, not a missing value. */
const fmt = (v?: number | string) => (isBlank(v) ? '—' : String(v));

/** Appends the unit unless the lender already sent one, so "12.9%" does not
 * become "12.9%%". */
const fmtApr = (v?: number | string) => {
  if (isBlank(v)) return '—';
  const s = String(v).trim();
  return s.endsWith('%') ? s : `${s}%`;
};

/** For extra-data columns whose HEADER says they are percentages ("Loan %").
 * Excel percent-formatted cells STORE the fraction (9.63% is 0.0963), and
 * that raw fraction is what the import keeps — so numeric values are
 * rendered Excel-style: ×100, 2dp. A value that already carries its own %
 * sign is kept as sent, and anything non-numeric stays verbatim. */
const fmtFractionPercent = (v?: string) => {
  if (isBlank(v)) return EMPTY;
  const s = String(v).trim();
  if (s.endsWith('%')) return s;
  const n = parseMoney(s);
  if (n === null) return s;
  return `${(n * 100).toFixed(2)}%`;
};

/** Formatter for an extra-data column, picked from its HEADER — the only
 * signal we have about a column nobody typed: "…%" renders Excel-style
 * percentages; a currency-sounding header (Charges, Amount, Cost, Price,
 * Fee, Commission, £) renders money; anything else stays verbatim, which
 * keeps reference-like values ("260125-000403") intact. */
const MONEYISH_HEADER = /£|charge|amount|cost|price|fee|commission|balance|payment/i;

const extraFormatter = (header: string) => {
  if (header.includes('%')) return fmtFractionPercent;
  if (MONEYISH_HEADER.test(header)) {
    return (v?: string) => (isBlank(v) ? EMPTY : fmtMoney(v));
  }
  return (v?: string) => fmt(v);
};

/** Dates normalised to ISO for comparison. The lender sends `receivedDate` and
 * `agreementStartDate` in whichever format the cell held, so comparing the raw
 * strings would order "03/08/2020" against "2020-08-03" by their first
 * character — stable, but meaningless. */
const isoKey = (v?: string) => {
  if (isBlank(v)) return '';
  const d = parseLenderDate(String(v));
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Natural compare, so AG-2 sorts before AG-13 rather than after it. Plain
 * string collation puts "13" before "2", which is what made the row order look
 * arbitrary even once it was stable. */
const naturalCompare = (a: string, b: string) =>
  a.localeCompare(b, 'en-GB', { numeric: true, sensitivity: 'base' });

/** Agreements read oldest-first within a claim, which is how a caseworker reads
 * a finance history. Ordered by start date, then by agreement number naturally.
 * Deliberately ASCENDING: descending made the tiebreak run Z->A, so a
 * 13-agreement claim listed AG-9, AG-8 ... AG-13, AG-12, AG-1. */
const compareAgreements = (a: LenderResponse, b: LenderResponse) => {
  const byStart = isoKey(a.agreementStartDate).localeCompare(isoKey(b.agreementStartDate));
  if (byStart !== 0) return byStart;
  return naturalCompare(String(a.agreementNumber || ''), String(b.agreementNumber || ''));
};

const sortKey = (r: LenderResponse) =>
  `${isoKey(r.receivedDate)}|${isoKey(r.agreementStartDate)}|${r.agreementNumber || ''}`;

/** Did this response actually notify us that commission was PAID? The 3-year
 * limitation clock runs from the date of knowledge, so a claim only gets the
 * timer when the lender's answer discloses commission — "No records found",
 * exceptions, unregulated agreements and the like start no clock.
 *
 * Two disclosure shapes exist:
 *  - Santander sends a four-way money breakdown. Any amount > 0 is a
 *    disclosure. All-zero is Santander explicitly saying NO commission was
 *    paid (1,848 located agreements do), so zero must NOT start a timer.
 *  - Batch 1.2 (BMW) sends no amounts at all, only a commissionModel
 *    ("Increasing DiC" etc.) — the model's presence IS the notification.
 * The model-only branch therefore applies only when the row carries no
 * amount fields whatsoever; a row that answers the money question with
 * zeros has answered it, whatever else it says. */
const COMMISSION_AMOUNT_FIELDS = [
  'increasingDicCommission', 'scaleCommission', 'fixedCommission', 'cpbCommission',
  // The recurring-template money fields (MBFS sends totalCommission): a zero
  // here is the lender answering "no commission was paid", so it must gate
  // the timer exactly as Santander's zeros do — the commissionModel-only
  // branch stays reserved for rows with NO amounts at all (Batch 1.2).
  'totalCommission', 'additionalCommissionAmount',
] as const;

const commissionNotified = (responses: LenderResponse[]): boolean =>
  responses.some((r) => {
    if (r.status === 'exception') return false;
    const amounts = COMMISSION_AMOUNT_FIELDS
      .map((f) => r[f])
      .filter((v) => !isBlank(v))
      .map((v) => parseMoney(v as number | string));
    if (amounts.length > 0) return amounts.some((n) => n !== null && n !== 0);
    return !isBlank(r.commissionModel);
  });

/** 3-year limitation timer, anchored to the claim's `submissionOutcomeDate` —
 * the date WE received the lender's response. NOT anchored to the rows'
 * `receivedDate`, which is the lender receiving OUR submission (on Batch 1.2
 * those differ by five months: 20 Feb vs 17 Jul).
 *
 * Deliberately a NEW, derived, read-only field: the Key Dates "Limitation"
 * entry is caseworker-managed and must not be touched by this. Because it is
 * computed at render time from the outcome date, future imports get the timer
 * for free — nothing extra to write.
 *
 * `today` is a parameter so tests pin the clock; callers omit it. */
const LIMITATION_YEARS = 3;

const limitationFromOutcome = (
  outcomeDate?: string | null,
  today: Date = new Date(),
): { expires: Date; daysLeft: number; label: string; severity: 'ok' | 'warning' | 'expired' } | null => {
  if (outcomeDate === null || isBlank(outcomeDate)) return null;
  const start = parseLenderDate(String(outcomeDate));
  if (!start) return null;

  const expires = new Date(start.getFullYear() + LIMITATION_YEARS, start.getMonth(), start.getDate());
  // 29 Feb + 3y would roll to 1 Mar; clamp back to the last day of Feb. Rolling
  // FORWARD would overstate the time available on a limitation deadline.
  if (expires.getMonth() !== start.getMonth()) expires.setDate(0);

  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysLeft = Math.round((expires.getTime() - startOfToday.getTime()) / 86_400_000);

  // Whole calendar months remaining, floored — "2 years 11 months" must never
  // read as more time than is actually left.
  let months = (expires.getFullYear() - startOfToday.getFullYear()) * 12
             + (expires.getMonth() - startOfToday.getMonth());
  if (expires.getDate() < startOfToday.getDate()) months -= 1;

  // Leftover days past the whole months: advance today by `months` calendar
  // months, clamped when the target month is shorter (31 Jan + 1m = 28 Feb,
  // not 3 Mar), then count the days to expiry.
  const afterMonths = new Date(
    startOfToday.getFullYear(), startOfToday.getMonth() + months, startOfToday.getDate());
  if (afterMonths.getDate() !== startOfToday.getDate()) afterMonths.setDate(0);
  const spareDays = Math.round((expires.getTime() - afterMonths.getTime()) / 86_400_000);

  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  let label: string;
  if (daysLeft < 0) {
    label = 'Limitation expired';
  } else if (daysLeft === 0) {
    label = 'Expires today';
  } else {
    // Written out in full — "2 years 11 months 14 days remaining". Zero parts
    // are dropped rather than printed ("2 years 5 days", not "2 years
    // 0 months 5 days"); when nothing but days remain, days alone.
    const parts: string[] = [];
    if (months >= 12) parts.push(unit(Math.floor(months / 12), 'year'));
    if (months % 12 > 0) parts.push(unit(months % 12, 'month'));
    if (spareDays > 0 || parts.length === 0) parts.push(unit(months > 0 ? spareDays : daysLeft, 'day'));
    label = `${parts.join(' ')} remaining`;
  }

  const severity: 'ok' | 'warning' | 'expired' =
    daysLeft < 0 ? 'expired' : daysLeft <= 183 ? 'warning' : 'ok';
  return { expires, daysLeft, label, severity };
};

/** What every formatter here returns for a value we do not have. A column where
 * EVERY response renders this carries no information for this claim, and is
 * dropped (see `liveColumns`). */
const EMPTY = '—';

const AGREEMENT_COLUMNS: Array<{
  label: string;
  render: (r: LenderResponse) => string;
  emphasis?: boolean;
}> = [
  { label: 'Agreement number', render: (r) => fmt(r.agreementNumber), emphasis: true },
  { label: 'Vehicle reg', render: (r) => fmt(r.vehicleRegistration) },
  { label: 'Commission model', render: (r) => fmt(r.commissionModel), emphasis: true },
  { label: 'Start', render: (r) => fmtDate(r.agreementStartDate) },
  { label: 'End', render: (r) => fmtDate(r.agreementEndDate) },
  { label: 'Early end', render: (r) => fmtDate(r.agreementEarlyEndDate) },
  { label: 'Term', render: (r) => fmt(r.agreementTerm) },
  { label: 'APR', render: (r) => fmtApr(r.apr) },
  { label: 'Cash price', render: (r) => fmtMoney(r.vehicleCashPrice) },
  { label: 'Financed', render: (r) => fmtMoney(r.financedAmount) },
  { label: 'Monthly', render: (r) => fmtMoney(r.monthlyRentals) },
  { label: 'Balloon', render: (r) => fmtMoney(r.balloonPayment) },
  // The commission breakdown, added for the Santander 30-Jun-2026 response
  // (columns AB-AF). Batch 1.2 carries none of these and Santander carries
  // none of Vehicle reg / Early end / Term / APR / Cash price / Financed /
  // Monthly / Balloon, which is exactly why the columns are now conditional --
  // showing all seventeen would give every claim eight dead columns.
  { label: 'DIC commission', render: (r) => fmtMoney(r.increasingDicCommission) },
  { label: 'Scale commission', render: (r) => fmtMoney(r.scaleCommission) },
  { label: 'Fixed commission', render: (r) => fmtMoney(r.fixedCommission) },
  { label: 'CPB commission', render: (r) => fmtMoney(r.cpbCommission) },
  { label: 'Additional', render: (r) => fmt(r.additionalCommission) },
  // Business minimum data set for the recurring template (24 Aug 2026) —
  // written by the admin import page; conditional like every other column,
  // so cohorts without them lose nothing.
  { label: 'Total commission', render: (r) => fmtMoney(r.totalCommission), emphasis: true },
  { label: 'Additional commission £', render: (r) => fmtMoney(r.additionalCommissionAmount) },
  { label: 'Total cost of credit', render: (r) => fmtMoney(r.totalCostOfCredit) },
];

/** "Submission Data Received" panel — what the lender returned for this claim.
 *
 * Shows EVERY agreement the lender sent, because the import stores one entry
 * per agreement and 441 claims carry more than one. An earlier version rendered
 * only the newest entry and labelled the rest "N earlier responses on file",
 * which was wrong twice over: siblings from one batch share a receivedDate, so
 * the visible one was arbitrary, and the hidden ones were not earlier — they
 * were separate agreements, each a potential claim. */
const LenderResponseCard: React.FC<Props> = ({
  responses,
  outcomeDate,
  commissionAmount,
  commissionInformedDate,
}) => {
  const responseRows = responses || [];
  const importedCommissionNotified = commissionNotified(responseRows);
  const extractedAmount = isBlank(commissionAmount)
    ? null
    : parseMoney(commissionAmount as number | string);
  const extractedCommissionNotified = extractedAmount !== null && extractedAmount !== 0;

  // Timer only when the response disclosed commission — the limitation clock
  // runs from the date of knowledge, and "No records found" is not knowledge.
  // Ordinary CAF correspondence uses its factual commissionInformedDate;
  // spreadsheet imports retain their existing submissionOutcomeDate fallback.
  const limitation = (importedCommissionNotified || extractedCommissionNotified)
    ? limitationFromOutcome(commissionInformedDate || outcomeDate)
    : null;

  if (responseRows.length === 0 && !limitation) return null;

  // Rows read oldest-first (compareAgreements); `newest` is a separate lookup,
  // used only for the received date in the header.
  const matched = responseRows.filter((r) => r.status !== 'exception').sort(compareAgreements);
  const exceptions = responseRows.filter((r) => r.status === 'exception').sort(compareAgreements);
  const allExceptions = matched.length === 0;

  const newest = [...responseRows].sort((a, b) => sortKey(b).localeCompare(sortKey(a)))[0];
  const lenders = Array.from(new Set(responseRows.map((r) => r.lender).filter(Boolean)));
  const sources = Array.from(new Set(responseRows.map((r) => r.source).filter(Boolean)));

  /** Only the columns this claim's lender actually answered.
   *
   * Different lenders return different shapes: Batch 1.2 (BMW) sent vehicle,
   * term, APR and payment figures; Santander sent a four-way commission
   * breakdown and none of the vehicle detail. A fixed column set gives whichever
   * cohort you are looking at a screenful of em-dashes and buries the columns
   * that do carry data.
   *
   * Derived from the rendered output rather than a per-column `has` predicate,
   * so whatever each formatter considers empty is what counts as empty — one
   * definition, no second place to keep in step. Note fmt(0) is "0" and
   * fmtMoney(0) is "£0.00", so a genuinely disclosed zero KEEPS its column:
   * 1,848 of Santander's located agreements disclose zero commission, which is
   * an answer, not a gap. */
  /** Columns for the lender's extra data — everything the import captured
   * verbatim because it had no typed field (MBFS Reference, Notes, Dealer
   * Name…). Derived from the union of additionalData keys across this
   * claim's agreements, labelled by the spreadsheet header as-is, and
   * subject to the same drop-when-all-empty rule as the typed columns.
   * A key that collides with a fixed column label is skipped: the typed
   * column is the curated one, and duplicate labels would also collide as
   * React keys. */
  const fixedLabels = new Set(AGREEMENT_COLUMNS.map((c) => c.label));
  const extraColumns = Array.from(
    new Set(matched.flatMap((r) => Object.keys(r.additionalData || {}))))
    .filter((key) => !fixedLabels.has(key))
    .map((key) => {
      const format = extraFormatter(key);
      return {
        label: key,
        render: (r: LenderResponse) => format(r.additionalData?.[key]),
        emphasis: false,
      };
    });

  const liveColumns = [...AGREEMENT_COLUMNS, ...extraColumns].filter((c) =>
    matched.some((r) => c.render(r) !== EMPTY));

  return (
    <div className="lender-response-card" style={styles.card}>
      {responseRows.length > 0 && <div
        style={{
          ...styles.header,
          background: allExceptions ? 'var(--color-warning-50)' : 'var(--color-success-50)',
        }}
      >
        <div>
          <span style={styles.eyebrow}>Submission Data Received</span>
          <h3 style={styles.title}>
            {allExceptions ? (
              <>
                ⚠️ Exception
                {exceptions.length === 1 && exceptions[0].exceptionReason
                  ? ` — ${exceptions[0].exceptionReason}`
                  : exceptions.length > 1
                  ? ` — ${exceptions.length} exceptions returned`
                  : ' — No data returned'}
              </>
            ) : (
              <>
                ✓ Lender response received
                {matched.length === 1
                  ? matched[0].commissionModel
                    ? ` — ${matched[0].commissionModel} commission`
                    : ''
                  : ` — ${matched.length} agreements`}
              </>
            )}
          </h3>
        </div>
        <div style={styles.meta}>
          <div>{lenders.join(', ') || '—'}</div>
          <div style={styles.metaSub}>
            {/* "Lender received", not "Received": this is the workbook's "Date
                Received" column, which is the LENDER receiving OUR submission —
                not us receiving their answer. It reads 2026-02-20 against a
                submissionSentDate of 2026-02-21, i.e. a day BEFORE we sent it, so
                labelling it "Received" implied we had the response in February
                when the file actually reached us on 17 July. When WE received it
                lives in submissionOutcomeDate, shown in the submission block.
                The Exceptions sheet has no date column, so exception-only claims
                have none at all — omit the clause rather than print an em-dash
                beside a response that demonstrably arrived. */}
            {!isBlank(newest.receivedDate) && `Lender received ${fmtDate(newest.receivedDate)}`}
            {!isBlank(newest.receivedDate) && sources.length ? ' · ' : ''}
            {sources.join(', ')}
          </div>
        </div>
      </div>}

      {/* 3-year limitation timer, counted from the date the commission
          disclosure reached us. A separate strip rather than a header clause:
          it is a deadline, not provenance, and it needs the red/amber state to
          survive whatever colour the header already has. */}
      {limitation && (
        <div
          style={{
            ...styles.limitationBar,
            ...(limitation.severity === 'expired'
              ? styles.limitationExpired
              : limitation.severity === 'warning'
              ? styles.limitationWarning
              : null),
          }}
        >
          <span style={styles.limitationName}>⏳ 3-year limitation</span>
          <span>
            {limitation.daysLeft < 0 ? 'Expired' : 'Expires'}{' '}
            {limitation.expires.toLocaleDateString('en-GB')}
          </span>
          <span style={styles.limitationCountdown}>{limitation.label}</span>
        </div>
      )}

      {/* One agreement: the field grid reads better than a one-row table. */}
      {matched.length === 1 && (
        <div style={styles.grid}>
          {liveColumns.map((c) => (
            <Field
              key={c.label}
              label={c.label}
              value={c.render(matched[0])}
              highlight={c.emphasis}
            />
          ))}
        </div>
      )}

      {/* Several agreements: a table, so every one is visible and comparable.
          No row numbers — an index next to rows a caseworker cannot reorder reads
          as if it means something. The agreement number is the identity, and it
          is pinned left so it stays on screen while the money columns are scrolled
          into view: on a 13-agreement claim, scrolling to read Balloon otherwise
          takes the agreement number and reg with it, and you cannot tell which
          row you are on. */}
      {matched.length > 1 && (
        <div style={styles.tableScroll}>
          <table style={styles.table}>
            <thead>
              <tr>
                {liveColumns.map((c, ci) => (
                  <th
                    key={c.label}
                    style={ci === 0 ? { ...styles.th, ...styles.stickyTh } : styles.th}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matched.map((r, i) => (
                <tr key={`${r.agreementNumber || 'row'}-${i}`}>
                  {liveColumns.map((c, ci) => (
                    <td
                      key={c.label}
                      style={{
                        ...styles.td,
                        ...(ci === 0 ? styles.stickyTd : null),
                        fontWeight: c.emphasis ? 700 : 400,
                        color: c.emphasis
                          ? 'var(--color-primary-700)'
                          : 'var(--color-neutral-800)',
                      }}
                    >
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Every exception is listed, whether or not the claim also has matched
          agreements. Listed unconditionally rather than only when there are
          several: a lone exception that names the agreement the lender could not
          trace still needs that number on screen, and the header only has room
          for the reason. */}
      {exceptions.length > 0 && (
        <div style={styles.exceptionList}>
          {exceptions.map((r, i) => (
            <div key={`exc-${r.agreementNumber || 'na'}-${i}`} style={styles.exceptionRow}>
              {allExceptions ? '' : '⚠️ '}
              {/* Labelled, not a bare "77720012513242: Proposal only" — that
                  reads as an unexplained number. Santander's "Proposal only"
                  rows DO carry a real agreement number (2,030 of them), so the
                  label has to say what the number is. */}
              {r.agreementNumber ? (
                <>
                  <span style={styles.exceptionLabel}>Agreement</span>{' '}
                  <span style={styles.exceptionNumber}>{r.agreementNumber}</span>
                  {' — '}
                </>
              ) : null}
              {r.exceptionReason || 'No data returned'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <div style={styles.field}>
    <div style={styles.fieldLabel}>{label}</div>
    <div
      style={{
        ...styles.fieldValue,
        fontWeight: highlight ? 700 : 500,
        color: highlight ? 'var(--color-primary-700)' : 'var(--color-neutral-800)',
      }}
    >
      {value}
    </div>
  </div>
);

/** Exported for unit tests only — these are the pure sort/format branches, and
 * testing them directly is cheaper and sharper than asserting on rendered DOM. */
export const __test = { parseLenderDate, parseMoney, fmtDate, fmtMoney, fmt, fmtApr,
  isBlank, sortKey, isoKey, naturalCompare, compareAgreements, limitationFromOutcome,
  commissionNotified };

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: '1px solid var(--color-neutral-200)',
    // --ds-radius is 10px (components/ui/ui.css); the fallback matches it so the
    // declared radius is the rendered one either way.
    borderRadius: 'var(--ds-radius, 10px)',
    overflow: 'hidden',
    marginBottom: 20,
    background: 'var(--color-neutral-0)',
    minWidth: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '14px 18px',
    gap: 16,
  },
  eyebrow: {
    fontSize: 'var(--text-xs)',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: 'var(--color-neutral-500)',
    fontWeight: 600,
  },
  title: { margin: '4px 0 0', fontSize: 'var(--text-lg)', color: 'var(--color-neutral-900)' },
  meta: {
    textAlign: 'right',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-neutral-700)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  metaSub: {
    fontWeight: 400,
    color: 'var(--color-neutral-500)',
    fontSize: 'var(--text-xs)',
    marginTop: 2,
  },
  // Limitation strip: turquoise while there's plenty of time; amber inside
  // 6 months; red once expired. A solid left accent plus a gradient wash —
  // it's the deadline on the card, so it should be the first thing the eye
  // lands on after the header.
  limitationBar: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    padding: '10px 18px',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-primary-900)',
    background:
      'linear-gradient(90deg, var(--color-primary-100) 0%, var(--color-primary-50) 100%)',
    border: '1px solid var(--color-success-500)',
    borderLeft: '4px solid var(--color-success-500)',
  },
  limitationWarning: {
    color: '#92400e',
    background:
      'linear-gradient(90deg, var(--color-warning-100) 0%, var(--color-warning-50) 100%)',
    borderLeft: '4px solid var(--color-warning-500)',
    borderBottom: '1px solid var(--color-warning-100)',
  },
  limitationExpired: {
    color: 'var(--color-error-700)',
    background:
      'linear-gradient(90deg, var(--color-error-100) 0%, var(--color-error-50) 100%)',
    borderLeft: '4px solid var(--color-error-500)',
    borderBottom: '1px solid var(--color-error-100)',
  },
  limitationName: {
    fontWeight: 700,
  },
  limitationCountdown: {
    fontWeight: 700,
    marginLeft: 'auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 1,
    background: 'var(--color-neutral-200)',
    padding: 1,
  },
  field: { background: 'var(--color-neutral-0)', padding: '10px 14px' },
  fieldLabel: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  fieldValue: { fontSize: 'var(--text-sm)', marginTop: 3 },
  // A wide table must scroll inside its own box rather than widening the drawer.
  // The card sets its own min-width:0 above; the drawer is a fixed-width column,
  // so this is belt-and-braces against the flex rule lanes.css:145 documents.
  tableScroll: {
    overflowX: 'auto',
    borderTop: '1px solid var(--color-neutral-200)',
    maxWidth: '100%',
  },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-sm)' },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    background: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)',
    fontSize: 'var(--text-xs)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--color-neutral-200)',
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-neutral-100)',
    whiteSpace: 'nowrap',
  },
  // Pinned identity column. Needs an opaque background of its own, otherwise the
  // scrolled cells show through underneath it, and a right border so it reads as
  // a frozen edge rather than a clipped cell.
  stickyTh: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    backgroundColor: 'var(--color-neutral-100)',
    borderRight: '1px solid var(--color-neutral-300)',
  },
  stickyTd: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    backgroundColor: 'var(--color-neutral-0)',
    borderRight: '1px solid var(--color-neutral-300)',
  },
  exceptionList: {
    borderTop: '1px solid var(--color-neutral-200)',
    background: 'var(--color-warning-50)',
    padding: '8px 18px',
  },
  exceptionRow: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-neutral-700)',
    padding: '3px 0',
  },
  // Mirrors the Field label treatment above, so an exception row reads the same
  // way as the matched rows rather than as a loose string.
  exceptionLabel: {
    fontSize: 'var(--text-xs)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: 'var(--color-neutral-500)',
  },
  exceptionNumber: {
    fontWeight: 600,
    color: 'var(--color-neutral-800)',
  },
};

export default LenderResponseCard;
